import React from 'react';
import { Route, Routes } from 'react-router-dom';
import './custom.css';
import { ChatPage } from './components/ChatPage';
import { DocumentsPanel } from './components/DocumentsPanel';
import { WizardPage } from './components/WizardPage';
import { LogPage } from './components/LogPage';
import { getTenantCode, needsHostResolution, resolveTenantFromHost } from './tenant';
import { FileTextOutlined, MessageOutlined } from '@ant-design/icons';

function MainApp() {
  const [isReady, setIsReady] = React.useState(false);
  const [tenantResolved, setTenantResolved] = React.useState(!needsHostResolution());
  const [activeTab, setActiveTab] = React.useState('chat');
  const [brand, setBrand] = React.useState(null);
  const [cookieConsent, setCookieConsent] = React.useState(null);
  const [tenantCode, setTenantCode] = React.useState(getTenantCode());
  const [tenantUnavailable, setTenantUnavailable] = React.useState(false);
  const [tenantUnavailableMessage, setTenantUnavailableMessage] = React.useState('');

  // Prima di tutto, risolvi il tenant dall'host se necessario
  React.useEffect(() => {
    if (tenantResolved) return;
    
    const initTenant = async () => {
      // Aspetta la risoluzione del tenant dall'host
      // Se trova un tenant, farà un reload della pagina
      const resolution = await resolveTenantFromHost();
      // Aggiorna il tenantCode dopo la risoluzione
      const resolvedCode = getTenantCode();
      setTenantCode(resolvedCode);
      if (!resolvedCode && needsHostResolution() && resolution?.unavailable) {
        setTenantUnavailable(true);
        setTenantUnavailableMessage(
          resolution?.detail || 'Il tenant richiesto attualmente non è disponibile.'
        );
      }
      setTenantResolved(true);
    };
    initTenant();
  }, [tenantResolved]);

  React.useEffect(() => {
    // Se stiamo ancora risolvendo il tenant dall'host, non fare nulla
    if (!tenantResolved) {
      return;
    }

    if (!tenantCode) {
      setBrand(null);
      // Se non c'è tenant code, siamo subito pronti (modalità admin)
      setIsReady(true);
      return;
    }

    const loadBrand = async () => {
      try {
        const res = await fetch('/api/admin/tenants', { method: 'GET' });
        if (!res.ok) return;
        const tenants = await res.json().catch(() => []);
        if (!Array.isArray(tenants)) return;
        const match = tenants.find(t => String(t.id).toLowerCase() === String(tenantCode).toLowerCase());
        if (!match) return;
        const payload = {
          displayName: match.displayName || '',
          logoUrl: match.logoUrl || null,
          logoBase64: match.logoBase64 || null,
          responseTone: match.responseTone || 'neutrale',
        };
        setBrand(payload);
        if (cookieConsent === 'accepted') {
          try {
            localStorage.setItem(`tenant_brand_${tenantCode}`, JSON.stringify(payload));
          } catch {
            // ignore cache errors
          }
        }
      } catch {
        // ignore branding errors
      } finally {
        // Pronto dopo il caricamento del brand (successo o errore)
        setIsReady(true);
      }
    };
    if (cookieConsent === 'accepted') {
      try {
        const cached = localStorage.getItem(`tenant_brand_${tenantCode}`);
        if (cached) {
          const parsed = JSON.parse(cached);
          if (parsed && typeof parsed === 'object') {
            setBrand(parsed);
          }
        }
      } catch {
        // ignore cache errors
      }
    }
    loadBrand();
  }, [cookieConsent, tenantCode, tenantResolved]);

  React.useEffect(() => {
    const title = brand?.displayName ? `${brand.displayName} | SportelloIA` : 'SportelloIA';
    document.title = title;
  }, [brand]);

  React.useEffect(() => {
    if (tenantCode) {
      setTenantUnavailable(false);
      setTenantUnavailableMessage('');
      setActiveTab('chat');
    }
  }, [tenantCode]);

  React.useEffect(() => {
    if (!tenantCode) {
      setCookieConsent(null);
      return;
    }
    try {
      const stored = localStorage.getItem(`cookie_consent_${tenantCode}`);
      if (stored === 'accepted') {
        setCookieConsent('accepted');
        return;
      }
    } catch {
      // ignore storage errors
    }
    setCookieConsent(null);
  }, [tenantCode]);

  const handleCookieConsent = (accepted) => {
    if (accepted) {
      try {
        localStorage.setItem(`cookie_consent_${tenantCode}`, 'accepted');
      } catch {
        // ignore storage errors
      }
      setCookieConsent('accepted');
    } else {
      setCookieConsent('declined');
    }
  };

  // Mostra loading fino a quando non è pronto
  if (!isReady) {
    return (
      <div className="app-shell">
        <div className="loading-screen">
          <div className="loading-spinner" />
          <span>Caricamento...</span>
        </div>
      </div>
    );
  }

  if (tenantUnavailable && !tenantCode) {
    return (
      <div className="app-shell">
        <div className="loading-screen">
          <div className="modal-card" style={{ maxWidth: 680 }}>
            <h3>Tenant temporaneamente non disponibile</h3>
            <p>{tenantUnavailableMessage || 'Il tenant richiesto attualmente non è disponibile.'}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app-shell">
      <div className="header-bar" />
      <div className="brand-fixed">
        <img className="brand-logo" src="/logo.png" alt="SportelloIA logo" />
        <span className="brand-name">SportelloIA - Smart Document Chat (BETA)</span>
      </div>
      {!tenantCode && (
        <nav className="tabs tabs--right" aria-label="Amministrazione">
          <button
            className={activeTab === 'documents' ? 'tab active' : 'tab'}
            onClick={() => setActiveTab('documents')}
            type="button"
          >
            <FileTextOutlined /> Tenant
          </button>
          <button
            className={activeTab === 'chat' ? 'tab active' : 'tab'}
            onClick={() => setActiveTab('chat')}
            type="button"
          >
            <MessageOutlined /> Chat IA
          </button>
        </nav>
      )}

      <div className={tenantCode ? 'tab-content tab-content--no-tabs' : 'tab-content'}>
        {activeTab === 'documents' && !tenantCode ? <DocumentsPanel /> : <ChatPage brand={brand} />}
      </div>

      <footer className="app-footer">
        <img className="footer-logo" src="/aspidea.png" alt="ASPidea logo" />
        <span>ASPidea Srl - P.iva 02716170788 - 2026 All Rights Reserved</span>
        <a href="/privacy.html" target="_blank" rel="noreferrer">Privacy</a>
      </footer>

      {tenantCode && cookieConsent === null && (
        <div className="modal-backdrop">
          <div className="modal-card">
            <h3>Preferenze cookie</h3>
            <p>
              Questo tenant richiede l&apos;autorizzazione a memorizzare cookie tecnici e preferenze
              per migliorare l&apos;esperienza. Puoi consultare la nostra <a href="/privacy.html" target="_blank" rel="noreferrer">informativa privacy</a>.
            </p>
            <div className="modal-actions">
              <button className="secondary-button" onClick={() => handleCookieConsent(false)}>
                Rifiuta
              </button>
              <button className="primary-button" onClick={() => handleCookieConsent(true)}>
                Accetta
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function App() {
  return (
    <Routes>
      <Route path="/wizard" element={<WizardPage />} />
      <Route path="/logs" element={<LogPage />} />
      <Route path="*" element={<MainApp />} />
    </Routes>
  );
}

export default App;
